import React from 'react';
import type { DayState } from '../../../utils/seatAvailability';
import './DayStrip.css';

export interface StripDay {
  /** ISO yyyy-mm-dd. */
  iso: string;
  /** Undefined while that day's availability is still loading. */
  state?: DayState;
}

interface Props {
  days: StripDay[];
  /** Chosen ISO day, or '' when nothing is picked. */
  value: string;
  onChange: (iso: string) => void;
  /** Opens the full calendar for anything beyond the strip. */
  onPickDate: () => void;
  isLoading?: boolean;
  labelledBy?: string;
  /**
   * Whether the calendar could offer a day this strip does not.
   *
   * False when the member's book-ahead window ends inside the strip — then the calendar would open
   * with every further day disabled, which reads as a broken control rather than an empty one.
   */
  canPickDate?: boolean;
}

const WEEKDAY = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' });

/** Parsed as UTC so a chip never shifts a day either side of midnight in the local zone. */
const isoToUtc = (iso: string): Date => new Date(`${iso}T00:00:00Z`);

const STATE_LABEL: Record<DayState, string> = {
  open: '',
  full: 'Full',
  closed: 'Closed',
};

/**
 * The next seven days as chips, plus a "Pick a date" chip for anything further out.
 *
 * Chips carry no slot counts. "10 times" told a member nothing they could act on and made every
 * chip a different width; the day answers one question — can we come that day — and the slot rows
 * below answer the rest.
 */
const DayStrip: React.FC<Props> = ({
  days, value, onChange, onPickDate, isLoading = false, labelledBy, canPickDate = true,
}) => (
  <>
    <div className="day-strip" role="group" aria-labelledby={labelledBy}>
      {days.map((day) => {
        const date = isoToUtc(day.iso);
        const weekday = WEEKDAY.format(date);
        const dayNum = date.getUTCDate();
        // Undefined state = still loading. Enabled, because pre-disabling a day we have not
        // checked would hide availability the member actually has.
        const blocked = day.state === 'full' || day.state === 'closed';
        const label = day.state ? STATE_LABEL[day.state] : '';
        return (
          <button
            key={day.iso}
            type="button"
            className="day-chip"
            aria-pressed={value === day.iso}
            aria-label={`${weekday} ${dayNum}${label ? `, ${label}` : ''}`}
            disabled={blocked}
            onClick={() => onChange(day.iso)}
          >
            <span className="day-chip-weekday">{weekday}</span>
            <span className="day-chip-date">{dayNum}</span>
            {label && <span className="day-chip-state">{label}</span>}
          </button>
        );
      })}

      {canPickDate && (
        <button
          type="button"
          className="day-chip day-chip-pick"
          onClick={onPickDate}
        >
          <span className="icon" aria-hidden="true">▦</span>
          <span className="label">Pick a date</span>
        </button>
      )}
    </div>
    {isLoading && <p className="day-strip-loading">Checking availability…</p>}
  </>
);

export default DayStrip;
